/**
 * Versioned HTTP contract — `v1` — for asynchronous background RUNS through the secure API
 * gateway (`services/idea-engine-api`), backing the web app's `Orchestrator` + `RunStore` ports
 * (IDEA-35 / ADR 0036).
 *
 * Same rationale as the other `./http/*` contracts (ADR 0001/0005/0023 §5): the api service
 * and the web app must parse the SAME shapes without importing one another. This is the
 * WEB↔GATEWAY surface — enqueue a run, read live status, cancel, retry. The worker↔Postgres
 * path (leasing tasks with `SKIP LOCKED`, writing results) is internal SQL, not a wire shape.
 *
 * The browser never names individual tasks: it asks for a run of a given `kind` and the
 * server expands it into the task set (12 stages + spec for `idea-generation`), mirroring
 * how `NewIdeaSchema` lets the server stamp authoritative fields. Breaking changes ship as
 * `v2`, never by mutation.
 */
import { z } from 'zod';
import { apiEnvelope, ModelSelectionSchema, StageKeySchema } from './v1.js';
import { OwnerScopeSchema } from './persistence-v1.js';
import { VERDICT_BANDS } from '../stages.js';

// ── Shared field shapes ──────────────────────────────────────────────────────

const id = z.string().min(1).max(64);
const RunStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']);
const RunErrorSchema = z.object({ kind: z.string().min(1).max(64), message: z.string().max(2000) });

/**
 * Structured task outcome — wire schema for {@link import('../run.js').TaskResult} (A5 /
 * IDEA-70). Deliberately TIGHT (parse, don't validate): a closed kind vocabulary, stage keys
 * from the shared enum, verdicts from `VERDICT_BANDS`, a bounded note, `.strict()` so unknown
 * keys reject — never an open bag riding the queue. Versioned by the `v` literal: breaking
 * changes ship as a `TaskResultV2Schema` alongside, never by mutation.
 */
export const TaskResultV1Schema = z
  .object({
    v: z.literal(1),
    kind: z.enum(['generation', 'analysis', 'refine', 'spec', 'evidence', 'need', 'snapshot', 'meta']),
    stagesTouched: z.array(StageKeySchema).min(1).max(16).optional(),
    verdict: z.enum(VERDICT_BANDS).optional(),
    costUsd: z.number().finite().nonnegative().optional(),
    note: z.string().min(1).max(280).optional(),
  })
  .strict();
export type TaskResultV1 = z.infer<typeof TaskResultV1Schema>;

// ── Persisted payloads (response shapes) ─────────────────────────────────────

/** Wire shape of a Run row (parsed on read by both sides). */
export const RunPayloadSchema = z.object({
  id,
  ownerId: id,
  schemaVersion: z.number().int().positive(),
  kind: z.string().min(1).max(64),
  ideaId: id.optional(),
  status: RunStatusSchema,
  createdAt: z.number().int().nonnegative(),
  startedAt: z.number().int().nonnegative().optional(),
  finishedAt: z.number().int().nonnegative().optional(),
  totalTasks: z.number().int().nonnegative(),
  completedTasks: z.number().int().nonnegative(),
  failedTasks: z.number().int().nonnegative(),
  correlationId: id,
  /** Who/what started the run (e.g. 'operator', 'jarvis:<turnId>') — IDEA-63 / A1. */
  initiator: z.string().min(1).max(120).optional(),
  error: RunErrorSchema.optional(),
});
export type RunPayload = z.infer<typeof RunPayloadSchema>;

/** Wire shape of a RunTask row. */
export const RunTaskPayloadSchema = z.object({
  id,
  runId: id,
  ownerId: id,
  schemaVersion: z.number().int().positive(),
  ideaId: id.optional(),
  correlationId: id.optional(),
  kind: z.string().min(1).max(120),
  seq: z.number().int().nonnegative(),
  status: RunStatusSchema,
  attempts: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
  startedAt: z.number().int().nonnegative().optional(),
  finishedAt: z.number().int().nonnegative().optional(),
  durationMs: z.number().int().nonnegative().optional(),
  costUsd: z.number().nonnegative().optional(),
  error: RunErrorSchema.optional(),
  /** Per-task parameters the server stamped at enqueue (e.g. `{ feedback }` on a refine task). */
  payload: z.record(z.unknown()).optional(),
  /** Structured outcome reported by `complete` (A5) — what the task changed; absent on legacy rows. */
  result: TaskResultV1Schema.optional(),
});
export type RunTaskPayload = z.infer<typeof RunTaskPayloadSchema>;

/** A run plus its tasks — the shape the live Run board reads. */
export const RunWithTasksPayloadSchema = z.object({
  run: RunPayloadSchema,
  tasks: z.array(RunTaskPayloadSchema),
});
export type RunWithTasksPayload = z.infer<typeof RunWithTasksPayloadSchema>;

// ── POST /v1/runs/create (enqueue) ───────────────────────────────────────────

/**
 * Request: enqueue a run. The server stamps id/owner/version/correlation, expands the
 * `kind` into its task set (`idea-generation` → 12 `stage:*` + `spec`), and returns the
 * created run + queued tasks immediately (no work is done in this request).
 */
export const CreateRunRequestSchema = OwnerScopeSchema.extend({
  kind: z.string().min(1).max(64),
  ideaId: id.optional(),
  /**
   * Optional run parameters (bounded). The server maps them onto the task(s) that need them —
   * e.g. `{ feedback }` for `refine:<stage>` and `spec` runs. Never trusted beyond the schema:
   * executors parse what they use.
   */
  payload: z.record(z.unknown()).optional(),
  /**
   * Run-level operator steer (IDEA-51): free-text guidance stamped by the server's task
   * expansion into EVERY stage/analyze/spec task payload, so one Regenerate-portal note
   * governs the whole run. Applied by prompts with relevance framing; absent ⇒ unchanged.
   */
  steer: z.string().min(1).max(2000).optional(),
  /**
   * Run-level operator model/thinking choice (IDEA-48 / ADR 0054). Validated fail-closed
   * at enqueue (an invalid selection queues nothing) and stamped by the server's task
   * expansion into EVERY task's `payload`, so one choice on the Generate button governs
   * the whole run. A task-level selection wins over a run-level one.
   */
  modelSelection: ModelSelectionSchema.optional(),
  /** Who/what is starting the run (e.g. 'operator', 'jarvis:<turnId>') — echoed on the
   *  run row + status feed so origin is attributable (IDEA-63 / A1). */
  initiator: z.string().min(1).max(120).optional(),
});
export type CreateRunRequest = z.infer<typeof CreateRunRequestSchema>;
/** Response: the freshly enqueued run + its queued tasks. */
export const RunResponseSchema = apiEnvelope(RunWithTasksPayloadSchema);
export type RunResponse = z.infer<typeof RunResponseSchema>;

// ── POST /v1/runs/get (live status) ──────────────────────────────────────────

/** Request: read one owned run's current status + tasks. */
export const GetRunRequestSchema = OwnerScopeSchema.extend({ id });
export type GetRunRequest = z.infer<typeof GetRunRequestSchema>;
// Response: `RunResponseSchema`.

// ── POST /v1/runs/list (owner's runs, newest first; optional idea filter) ─────

/** Request: list the owner's runs, optionally scoped to one idea. */
export const ListRunsRequestSchema = OwnerScopeSchema.extend({ ideaId: id.optional() });
export type ListRunsRequest = z.infer<typeof ListRunsRequestSchema>;
/** Response: the runs (without tasks; fetch a run for its tasks). */
export const ListRunsResponseSchema = apiEnvelope(z.array(RunPayloadSchema));
export type ListRunsResponse = z.infer<typeof ListRunsResponseSchema>;

// ── POST /v1/runs/cancel ─────────────────────────────────────────────────────

/** Request: cancel an owned run — its non-terminal tasks move to `cancelled`. */
export const CancelRunRequestSchema = OwnerScopeSchema.extend({ id });
export type CancelRunRequest = z.infer<typeof CancelRunRequestSchema>;
// Response: `RunResponseSchema` (the run after cancellation).

// ── POST /v1/runs/retry-task ─────────────────────────────────────────────────

/** Request: re-queue a single failed task (drives the Run board's Retry affordance). */
export const RetryTaskRequestSchema = OwnerScopeSchema.extend({ runId: id, taskId: id });
export type RetryTaskRequest = z.infer<typeof RetryTaskRequestSchema>;
// Response: `RunResponseSchema` (the run with the task re-queued).

// ── Worker transitions (server-internal; the headless worker ↔ gateway) ──────
// These mutate the queue, so they are gated like the rest of /api (Cloudflare Access) and
// carry no principal — the worker runs across owners. Not called by the browser.

/** Request: claim the next ready task (no body fields today; a worker id may ride later). */
export const LeaseTaskRequestSchema = z.object({});
export type LeaseTaskRequest = z.infer<typeof LeaseTaskRequestSchema>;
/** Response: the leased task, or `null` when nothing is ready. */
export const LeaseTaskResponseSchema = apiEnvelope(RunTaskPayloadSchema.nullable());
export type LeaseTaskResponse = z.infer<typeof LeaseTaskResponseSchema>;

/**
 * Request: append tasks to an IN-FLIGHT run (IDEA-57 / ADR 0058 §3 — dynamic fan-out). The
 * evidence `ev-search` step discovers how many sources exist at runtime and appends the
 * per-source fetch/extract tasks (+ the final qualify) to the run it is executing in. Dedup on
 * the run's `unique (run_id, kind)`: re-appending an existing kind is a no-op, so redelivery is
 * idempotent. Server-internal (worker↔gateway, gated like lease/complete); no principal.
 */
export const AppendRunTasksRequestSchema = z.object({
  runId: id,
  tasks: z
    .array(
      z.object({
        kind: z.string().min(1).max(120),
        payload: z.record(z.unknown()).optional(),
      }),
    )
    .min(1)
    .max(64),
});
export type AppendRunTasksRequest = z.infer<typeof AppendRunTasksRequestSchema>;
// Response: `RunResponseSchema` (the run with the appended tasks), or 404 when the run is
// absent or already terminal (a cancelled run must not resurrect via fan-out).

/** Request: mark a leased task succeeded with its timing/cost + optional structured outcome (A5). */
export const CompleteTaskRequestSchema = z.object({
  taskId: id,
  durationMs: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative().optional(),
  result: TaskResultV1Schema.optional(),
});
export type CompleteTaskRequest = z.infer<typeof CompleteTaskRequestSchema>;

/** Request: record a failed attempt (re-queues under `maxAttempts`, else terminally fails). */
export const FailTaskRequestSchema = z.object({
  taskId: id,
  error: RunErrorSchema,
  durationMs: z.number().int().nonnegative(),
  maxAttempts: z.number().int().nonnegative(),
});
export type FailTaskRequest = z.infer<typeof FailTaskRequestSchema>;
// Complete/Fail response: `RunResponseSchema` (the rolled-forward run), or 404 if the task is gone.

// ── Runner observability (IDEA-35 diagnosis 2026-07-05) ─────────────────────
// The async pipeline's failure modes were invisible (swallowed pokes, containers exiting 0
// on unreachable queues, no trace of whether the worker ever booted). Every layer now reports
// structured RUNNER EVENTS through the gateway into the DB, so "did the wake fire / did the
// container boot / did the drain fail" is answerable with one API call from anywhere — no
// Cloudflare dashboard required. Low-volume by design: lifecycle transitions only, never
// per-poll or per-render.

/** Where a runner event was observed. */
export const RunnerEventSourceSchema = z.enum(['gateway', 'do', 'worker', 'web']);
export type RunnerEventSource = z.infer<typeof RunnerEventSourceSchema>;

/** One persisted runner lifecycle event (newest-first in reads). */
export const RunnerEventSchema = z.object({
  atMs: z.number().int().nonnegative(),
  source: RunnerEventSourceSchema,
  /** e.g. run.enqueued | wake.sent | wake.failed | cron.wake | worker.boot | drain.start | drain.stop | lease.error */
  kind: z.string().min(1).max(64),
  runId: id.optional(),
  taskId: id.optional(),
  correlationId: id.optional(),
  /** Free-form context (status codes, outcome, config summary) — human-readable, not parsed. */
  detail: z.string().max(4000).optional(),
});
export type RunnerEvent = z.infer<typeof RunnerEventSchema>;

/** Request: append one runner event (worker/DO-internal; the gateway stamps the time). */
export const RecordRunnerEventRequestSchema = z.object({
  source: RunnerEventSourceSchema,
  kind: z.string().min(1).max(64),
  runId: id.optional(),
  taskId: id.optional(),
  correlationId: id.optional(),
  detail: z.string().max(4000).optional(),
});
export type RecordRunnerEventRequest = z.infer<typeof RecordRunnerEventRequestSchema>;
/** Response: acknowledgement. */
export const RecordRunnerEventResponseSchema = apiEnvelope(z.object({ recorded: z.literal(true) }));
export type RecordRunnerEventResponse = z.infer<typeof RecordRunnerEventResponseSchema>;

/**
 * The queue + runner state in one read — the async pipeline's diagnostics surface.
 * `stalledRunningTasks` counts `running` tasks past the lease visibility timeout (crashed or
 * evicted worker; reclaim-eligible) — a non-zero value with an idle worker is the #1 "it's
 * stuck" signal. `oldestQueuedTaskAgeMs` > a minute with a deployed worker means wakes are
 * not reaching the container.
 */
export const RunsOverviewSchema = z.object({
  queuedTasks: z.number().int().nonnegative(),
  runningTasks: z.number().int().nonnegative(),
  queuedRuns: z.number().int().nonnegative(),
  runningRuns: z.number().int().nonnegative(),
  oldestQueuedTaskAgeMs: z.number().int().nonnegative().nullable(),
  stalledRunningTasks: z.number().int().nonnegative(),
  /** Newest-first tail of runner events (bounded — a diagnostic window, not a log store). */
  lastEvents: z.array(RunnerEventSchema),
});
export type RunsOverview = z.infer<typeof RunsOverviewSchema>;

/** Request: read the runs overview (empty body; POST for /v1 uniformity). */
export const RunsOverviewRequestSchema = z.object({});
export type RunsOverviewRequest = z.infer<typeof RunsOverviewRequestSchema>;
/** Response: the overview. */
export const RunsOverviewResponseSchema = apiEnvelope(RunsOverviewSchema);
export type RunsOverviewResponse = z.infer<typeof RunsOverviewResponseSchema>;
